import { Text, View, StyleSheet } from '@react-pdf/renderer'

const pdfStyles = StyleSheet.create({
  heading: { fontWeight: 500, textAlign: 'center', paddingVertical: 0 },
  headingDark: { backgroundColor: '#111827', color: '#ffffff' },
  headingLight: { backgroundColor: '#ffffff', color: '#111827' },
  content: { paddingHorizontal: 2, paddingTop: 2, marginBottom: 2 },
  contentDark: { backgroundColor: '#111827', color: '#ffffff' },
  contentLight: { backgroundColor: '#ffffff', color: '#111827' },
  contact: { fontWeight: 500 },
  contactDarkThemed: { paddingHorizontal: 1, paddingBottom: 4, backgroundColor: '#111827', color: '#ffffff' },
  contactLightThemed: { paddingHorizontal: 1, paddingBottom: 4, backgroundColor: '#ffffff', color: '#111827' },
  contactValue: { fontSize: 10.5, fontWeight: 'bold', color: '#111827' }, // numbers
  email: { color: '#111827', fontSize: 9 }, // email
  agency: { fontSize: 6, fontWeight: 200, color: '#111827', textAlign: 'right' },
});

// Fixed: contact formatter (simple Text only)
function formatContactPDF(value) {
  if (!value) return null;

  // Match: prefix + number + suffix (like "Mob: +911234567890 (Home)")
  const match = value.match(/^(.*?)(\+?\d{8,15})(.*)$/);

  let prefix = '';
  let numberPart = value;
  let suffix = '';

  if (match) {
    prefix = match[1]?.trim() || '';
    numberPart = match[2]?.trim() || '';
    suffix = match[3]?.trim() || '';
  }

  return (
    <Text style={{ marginTop: -2 }}>
      {prefix ? (
        <Text style={{ fontSize: 6 }}>
          {prefix}
        </Text>
      ) : null}
      {numberPart ? (
        <Text style={{ fontSize: 10.5, fontFamily: 'Arial', fontWeight: 500, lineHeight: 1 }}>
          {numberPart}
        </Text>
      ) : null}
      {suffix ? (
        <Text style={{ fontSize: 6 }}>
          {suffix}
        </Text>
      ) : null}
    </Text>
  );
}

// Card for each classified
const ClassifiedCard = ({ item }) => {
  if (!item) return null;

  // Font sizes (convert px → pt)
  let fontSize = item.contentfs ? parseInt(String(item.contentfs).replace('pt', '')) : 8;
  let headingFontSize = item.hline1fs ? parseInt(String(item.hline1fs).replace('pt', '')) : 9;

  const headingTheme = item.hlinebg === 'Dark' ? pdfStyles.headingDark : pdfStyles.headingLight;
  const contentTheme = item.contentbg === 'Dark' ? pdfStyles.contentDark : pdfStyles.contentLight;
  const contactTheme = item.contactbg === 'Dark' ? pdfStyles.contactDarkThemed : pdfStyles.contactLightThemed;

  // Combine heading
  const headingLines = [item.hline1 || '', item.hline2 || '', item.hline3 || '']
    .map((line) => (line || '').toString().trim())
    .filter(Boolean);

  const isEnglish = (item.language || '').toLowerCase() === 'english';

  let combinedHeading;

  if (isEnglish) {
    const hasBoyOrGirl = headingLines.some((line) => {
      const lower = line.toLowerCase();
      return lower.includes('boy') || lower.includes('girl');
    });

    if (hasBoyOrGirl) {
      combinedHeading = headingLines.join('\n');
    } else {
      const hlinesCombined = headingLines.join('\n');
      const genderTrimmed = (item.gender || '').toString().trim();

      combinedHeading = hlinesCombined && genderTrimmed
        ? `${hlinesCombined} ${genderTrimmed}`
        : hlinesCombined || genderTrimmed;
    }

  } else {
    // 🔴 Non-English → NO gender logic at all
    combinedHeading = headingLines.join('\n');
  }

  // Default size (cm)
  const defaultHeight = 10;
  const defaultWidth = 3.8;

  let cardWidthCm = defaultWidth;
  let cardHeightCm = defaultHeight;

  // If DB sends only a number, use it as height
  if (item.size && !isNaN(parseFloat(item.size))) {
    cardHeightCm = parseFloat(item.size);
  }

  const POINTS_PER_CM = 28.346;
  const cardStyle = {
    width: cardWidthCm * POINTS_PER_CM,   // always 3.8cm
    height: cardHeightCm * POINTS_PER_CM, // from DB or default
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'black',
    backgroundColor: '#ffffff',
    position: 'relative', // needed for absolute children (e.g., agency)
  };

  const displayCrn = item.crn
    ? `${item.agency}/${String(item.crn).slice(-4)}`
    : `${item.agency}/****`;

  console.log("crn:", displayCrn);

  const isUrdu = (item.language || '').toLowerCase() === 'urdu';

  return (
    <View style={cardStyle}>
      {combinedHeading && (
        <View style={[pdfStyles.heading, headingTheme]}>
          <Text style={{ fontSize: headingFontSize, fontFamily: 'Arial', fontWeight: 200, paddingBottom: 1, lineHeight: 1 }} hyphenationCallback={(word) => word.split(/(?=\s)/)}>
            {combinedHeading}
          </Text>
        </View>
      )}

      {item.content && (
        <View style={[pdfStyles.content, contentTheme]}>
          <Text
            style={{
              fontSize,
              lineHeight: 1.4,

              // 🔥 Key Fix
              textAlign: isUrdu ? 'right' : 'justify',
              direction: isUrdu ? 'rtl' : 'ltr',
            }}

            hyphenationCallback={(word) => {
              if (isUrdu) return [word];
              if (word.length <= cardStyle.width) return [word];
              const chunks = [];
              for (let i = 0; i < word.length; i += cardStyle.width) {
                chunks.push(word.slice(i, i + cardStyle.width));
              }
              return chunks;
            }}
          >
            {item.content}
          </Text>
        </View>
      )}

      {(item.whatsapp || item.mobile || item.email) && (
        <View style={contactTheme}>
          {item.whatsapp && formatContactPDF(item.whatsapp)}
          {item.mobile && formatContactPDF(item.mobile)}
          {item.email && (
            <Text style={{ fontSize: 9, color: '#111827' }}>{item.email}</Text>
          )}
        </View>
      )}


      {/* Agency inside card, bottom-right with white bg */}
      {item.agency && (
        <View
          style={{
            position: 'absolute',
            bottom: -5,
            right: 4,
            backgroundColor: '#ffffff',
            paddingHorizontal: 1,
            margin: 0,
            zIndex: -999

          }}
        >
          <Text style={pdfStyles.agency}>{displayCrn}</Text>
        </View>
      )}
    </View>
  );
};

export default ClassifiedCard